import { Controller } from "@hotwired/stimulus"

// Loading Form Controller - Shows a full-page overlay during form submission
//
// Usage:
//   <form data-controller="loading-form"
//         data-loading-form-message-value="Processing..."
//         data-loading-form-submitting-class="opacity-50 pointer-events-none">
//     <button type="submit">Submit</button>
//   </form>
//
// For mode-specific messages (like article generation):
//   Add data-loading-form-mode-messages-value as JSON with mode keys
//   Add a radio button group with name matching data-loading-form-mode-field-value
//
export default class extends Controller {
  static values = {
    message: { type: String, default: "Processing... please wait" },
    modeField: { type: String, default: "mode" },
    modeMessages: { type: Object, default: {} }
  }

  static classes = ["submitting"]

  connect() {
    this.overlay = null
    this.element.addEventListener("submit", this.handleSubmit.bind(this))
  }

  disconnect() {
    this.removeOverlay()
    this.element.removeEventListener("submit", this.handleSubmit.bind(this))
  }

  handleSubmit(event) {
    // Prevent double submission
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    this.isSubmitting = true
    this.showOverlay()
    this.disableSubmitButtons()

    // Add submitting class if defined
    if (this.hasSubmittingClass) {
      this.element.classList.add(this.submittingClass)
    }
  }

  getMessage() {
    // Check if we have mode-specific messages and a mode field
    if (Object.keys(this.modeMessagesValue).length > 0) {
      const modeInput = this.element.querySelector(`input[name="${this.modeFieldValue}"]:checked`)
      if (modeInput && this.modeMessagesValue[modeInput.value]) {
        return this.modeMessagesValue[modeInput.value]
      }
    }
    return this.messageValue
  }

  showOverlay() {
    const message = this.getMessage()

    this.overlay = document.createElement("div")
    this.overlay.id = "loading-overlay"
    this.overlay.className = "fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50"
    this.overlay.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
        <div class="mb-4">
          <svg class="animate-spin h-12 w-12 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Generating Content</h3>
        <p class="text-gray-600 mb-4">${message}</p>
        <p class="text-sm text-gray-400">Please don't close or refresh this page.</p>
      </div>
    `

    document.body.appendChild(this.overlay)
    document.body.style.overflow = "hidden"
  }

  removeOverlay() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
      document.body.style.overflow = ""
    }
  }

  disableSubmitButtons() {
    const buttons = this.element.querySelectorAll('button[type="submit"], input[type="submit"]')
    buttons.forEach(button => {
      button.disabled = true
      button.classList.add("opacity-50", "cursor-not-allowed")
    })
  }
}
